import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, PlayCircle, RotateCcw, Wrench, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api";
import { MIGRATION } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { GymLoader } from "../../components/common/GymLoader";
import { ErrorState } from "../../components/common/ErrorState";
import { RollbackDialog } from "../../components/migration/RollbackDialog";

const STATUS_META = {
  draft: { label: "Draft", tone: "#8B8B92" },
  previewed: { label: "Previewed", tone: "#C9A227" },
  committing: { label: "Committing\u2026", tone: "#C9A227" },
  committed: { label: "Committed", tone: "#3FA65B" },
  rolled_back: { label: "Rolled back", tone: "#8B8B92" },
};

// UX-P04 SCR-P04-01 - Migration Studio home: every migration this org has ever started,
// draft-through-committed, with resume/view/rollback actions per row.
export default function MigrationHome() {
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rollbackTarget, setRollbackTarget] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["migrations"],
    queryFn: () => api.get("/migrations").then((r) => r.data.migrations),
  });

  const discardMutation = useMutation({
    mutationFn: (id) => api.post(`/migration/${id}/discard`),
    onSuccess: () => { toast.success("Draft discarded"); queryClient.invalidateQueries({ queryKey: ["migrations"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Could not discard this draft"),
  });

  const rollbackMutation = useMutation({
    mutationFn: (id) => api.post(`/migration/${id}/rollback`),
    onSuccess: () => {
      toast.success("Migration rolled back \u2014 reversing entries posted, migrated members voided");
      setRollbackTarget(null);
      queryClient.invalidateQueries({ queryKey: ["migrations"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Rollback failed"),
  });

  if (isLoading) return <GymLoader />;
  if (error) return <ErrorState message="Couldn't load migrations." />;

  return (
    <div className="space-y-5 max-w-5xl" data-testid={MIGRATION.homePage}>
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Migration Studio</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Bring a gym's existing members, plans and dues in - safely, and resumably.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid={MIGRATION.homeToolsButton} variant="outline" onClick={() => navigate("/app/migration/tools")}>
            <Wrench size={15} className="mr-1.5" /> Tools
          </Button>
          <Button data-testid={MIGRATION.homeStartButton} onClick={() => navigate("/app/migration/wizard/new")} style={{ background: "var(--scarlet-accent)", color: "#fff" }}>
            <PlayCircle size={15} className="mr-1.5" /> Start new migration
          </Button>
        </div>
      </motion.div>

      {(!data || data.length === 0) && (
        <motion.div {...fadeUp(reducedMotion, 0.05)} className="solid-surface rounded-[14px] p-8 text-center">
          <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No migrations yet - start one to bring a gym's existing members in.</p>
        </motion.div>
      )}

      <div className="space-y-2.5">
        {data?.map((b, i) => {
          const meta = STATUS_META[b.status] || { label: b.status, tone: "#8B8B92" };
          return (
            <motion.div
              key={b.id} {...fadeUp(reducedMotion, 0.03 * i)} data-testid={MIGRATION.homeRow(b.id)}
              className="solid-surface rounded-[14px] p-4 flex items-center justify-between gap-4 flex-wrap"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>
                    {b.source_files.map((f) => f.name).join(", ") || "(no files yet)"}
                  </span>
                  <Badge style={{ background: `${meta.tone}22`, color: meta.tone, border: "none" }}>{meta.label}</Badge>
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>
                  {b.counts.clean} clean &middot; {b.counts.flagged} flagged &middot; {b.counts.duplicates} duplicates &nbsp;&middot;&nbsp; updated {new Date(b.updated_at).toLocaleString()}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {b.status === "draft" && (
                  <>
                    <Button data-testid={MIGRATION.homeResumeButton(b.id)} size="sm" onClick={() => navigate(`/app/migration/wizard/${b.id}`)}>
                      Resume <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                    <Button data-testid={MIGRATION.homeDiscardButton(b.id)} size="sm" variant="outline" onClick={() => discardMutation.mutate(b.id)}>
                      <XCircle size={14} className="mr-1.5" /> Discard
                    </Button>
                  </>
                )}
                {b.status === "previewed" && (
                  <Button data-testid={MIGRATION.homeResumeButton(b.id)} size="sm" onClick={() => navigate(`/app/migration/wizard/${b.id}`)}>
                    Resume at Preview <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                )}
                {(b.status === "committed" || b.status === "committing") && (
                  <>
                    <Button data-testid={MIGRATION.homeViewButton(b.id)} size="sm" variant="outline" onClick={() => navigate(`/app/migration/wizard/${b.id}`)}>View</Button>
                    <Button data-testid={MIGRATION.homeRollbackButton(b.id)} size="sm" variant="destructive" onClick={() => setRollbackTarget(b)}>
                      <RotateCcw size={14} className="mr-1.5" /> Rollback
                    </Button>
                  </>
                )}
                {b.status === "rolled_back" && (
                  <Button data-testid={MIGRATION.homeViewButton(b.id)} size="sm" variant="outline" onClick={() => navigate(`/app/migration/wizard/${b.id}`)}>View</Button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <RollbackDialog
        open={!!rollbackTarget} onOpenChange={(v) => !v && setRollbackTarget(null)}
        onConfirm={() => rollbackMutation.mutate(rollbackTarget.id)} pending={rollbackMutation.isPending}
      />
    </div>
  );
}
